import { CatalogState, defaultCatalogState, FetchCatalogsResponsePayload } from './catalog.types';
import { cloneDeep } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { EmptyCatalog } from '@/types/Catalog';
import { EmptySeller } from '@/types/Seller';
import { getAddressForCatalog } from '@/utils/address';
import {
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_ENDED_ACTION,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_OPENED_ACTION,
    LIVE_AUCTION_STARTED,
    LIVE_AUCTION_STARTED_ACTION,
    LiveAuctionStatusLoaded,
    LOAD_HOME_DATA_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS_ACTION,
    LOAD_UPCOMING_CATALOGS_SUCCESS,
} from '@/redux/modules/actions';
import { LOAD_SELLER_ENDED_CATALOGS_SUCCESS } from '../sellerEndedCatalogs';
import { LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS } from '../sellerUpcomingCatalogs';
import { loadCatalogs } from './catalog.actions';
import { PaymentProviders } from '@/enums';
import { safelyEscapeText } from '@/utils/safelyRender';
import { TypedActionWithPayload } from '@/types/redux';
import difference from 'lodash/difference';
import union from 'lodash/union';

// As we replace more areas with redux toolkit thunks and actions and whatnot,
// the usage of these functions in the extraReducers should eventually be reduced to 0.
// At that time, these functions and types should be deleted
// region Old Redux Functions
type OldSuccessTypes =
    | typeof LOAD_HOME_DATA_SUCCESS
    | typeof LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS
    | typeof LOAD_SELLER_ENDED_CATALOGS_SUCCESS
    | typeof LOAD_UPCOMING_CATALOGS_SUCCESS;

export type OldSuccessAction = TypedActionWithPayload<
    OldSuccessTypes,
    FetchCatalogsResponsePayload,
    { actionTime: number }
>;

const updateCatalogsOnOldSuccessAction = (state: CatalogState, action: OldSuccessAction): CatalogState => {
    const existing = cloneDeep(state.byId);
    const loaded = cloneDeep(state.loaded);
    let loading = state.loading.slice();
    const { actionTime } = action.meta;

    const { payload } = action;
    payload?.catalogs?.forEach((catalog) => {
        let paymentProvider = PaymentProviders.Unknown;
        if (catalog.highRiskPayments) {
            paymentProvider = PaymentProviders.Paysafe;
        } else if (catalog.payrixPayments) {
            paymentProvider = PaymentProviders.Payrix;
        }

        const seller = payload.sellers?.find((s) => s.sellerId === catalog.sellerId) || EmptySeller;
        let address = catalog.address;
        let sellerName: string;
        if (seller.sellerId) {
            address = getAddressForCatalog(address, seller);
            sellerName = seller.name;
        }

        existing[catalog.catalogId] = {
            ...catalog,
            address,
            paymentProvider,
            sellerName,
            title: safelyEscapeText(catalog.title),
        };

        const coverLots = action.payload?.coverLots?.find(
            (coverLot) => coverLot.catalogId === catalog.catalogId
        )?.coverLots;
        existing[catalog.catalogId].coverLots = coverLots || [];

        delete existing[catalog.catalogId].payrixPayments;
        loaded[catalog.catalogId] = actionTime;
        loading = difference(loading, [catalog.catalogId]);
    });

    return {
        ...state,
        byId: existing,
        loaded,
        loading,
    };
};

const updateCatalogsOnOldLiveAuctionEndedAction = (
    state: CatalogState,
    action: LIVE_AUCTION_ENDED_ACTION
): CatalogState => ({
    ...state,
    byId: {
        ...state.byId,
        [action.payload.catalogId]: {
            ...state.byId[action.payload.catalogId],
            catalogStatus: 'done',
        },
    },
});

const updateCatalogsOnOldLoadLiveCatalogStatusSuccessAction = (
    state: CatalogState,
    action: LOAD_LIVE_CATALOG_STATUS_SUCCESS_ACTION
): CatalogState => {
    const { catalogId, status } = action.payload as LiveAuctionStatusLoaded;
    const existing = cloneDeep(state.byId);
    if (catalogId) {
        existing[catalogId] = existing[catalogId] || EmptyCatalog;
        if (status === 'done' && existing[catalogId].catalogStatus !== 'done') {
            existing[catalogId].catalogStatus = 'done';
        } else if (status === 'live' && existing[catalogId].catalogStatus !== 'done') {
            existing[catalogId].catalogStatus = 'live';
        }
    }
    return {
        ...state,
        byId: existing,
    };
};

const updateCatalogsOnOldLiveAuctionOpenedOrStarted = (
    state: CatalogState,
    action: LIVE_AUCTION_OPENED_ACTION | LIVE_AUCTION_STARTED_ACTION
): CatalogState => {
    const { catalogId } = action.payload;
    const existing = cloneDeep(state.byId);
    existing[catalogId] = existing[catalogId] || cloneDeep(EmptyCatalog);
    existing[catalogId].catalogStatus = 'live';
    return {
        ...state,
        byId: existing,
    };
};
// endregion

const catalogSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(loadCatalogs.pending, (state, { meta }) => {
            state.loading = union(state.loading, meta.arg);
        });
        builder.addCase(loadCatalogs.fulfilled, (state, { payload }) => {
            const { actionTime, data } = payload;
            data.catalogs.forEach((catalog) => {
                let paymentProvider = PaymentProviders.Stripe;
                if (catalog.highRiskPayments) {
                    paymentProvider = PaymentProviders.Paysafe;
                } else if (catalog.payrixPayments) {
                    paymentProvider = PaymentProviders.Payrix;
                }

                // ensure that the catalog has the address data to avoid having to call getAddressForCatalog during render
                const seller = payload.data.sellers?.find((s) => s.sellerId === catalog.sellerId) || EmptySeller;
                let address = catalog.address;
                let sellerName: string;
                if (seller) {
                    address = getAddressForCatalog(address, seller);
                    sellerName = seller.name;
                }

                const coverLots =
                    data.coverLots.find((coverLot) => coverLot.catalogId === catalog.catalogId)?.coverLots || [];
                state.byId[catalog.catalogId] = {
                    ...catalog,
                    address,
                    coverLots,
                    paymentProvider,
                    sellerName,
                    title: safelyEscapeText(catalog.title),
                };
                delete state.byId[catalog.catalogId].payrixPayments;

                state.loading = difference(state.loading, [catalog.catalogId]);
                state.loaded[catalog.catalogId] = actionTime;
            });
        });
        builder.addCase(loadCatalogs.rejected, (state, action) => {
            state.loading = difference(state.loading, action.meta.arg);
        });
        builder.addCase<string, OldSuccessAction>(LOAD_HOME_DATA_SUCCESS, updateCatalogsOnOldSuccessAction);
        builder.addCase<string, OldSuccessAction>(
            LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS,
            updateCatalogsOnOldSuccessAction
        );
        builder.addCase<string, OldSuccessAction>(LOAD_SELLER_ENDED_CATALOGS_SUCCESS, updateCatalogsOnOldSuccessAction);
        builder.addCase<string, OldSuccessAction>(LOAD_UPCOMING_CATALOGS_SUCCESS, updateCatalogsOnOldSuccessAction);
        builder.addCase<string, LIVE_AUCTION_ENDED_ACTION>(
            LIVE_AUCTION_ENDED,
            updateCatalogsOnOldLiveAuctionEndedAction
        );
        builder.addCase<string, LOAD_LIVE_CATALOG_STATUS_SUCCESS_ACTION>(
            LOAD_LIVE_CATALOG_STATUS_SUCCESS,
            updateCatalogsOnOldLoadLiveCatalogStatusSuccessAction
        );
        builder.addCase<string, LIVE_AUCTION_OPENED_ACTION>(
            LIVE_AUCTION_OPENED,
            updateCatalogsOnOldLiveAuctionOpenedOrStarted
        );
        builder.addCase<string, LIVE_AUCTION_STARTED_ACTION>(
            LIVE_AUCTION_STARTED,
            updateCatalogsOnOldLiveAuctionOpenedOrStarted
        );
    },
    initialState: defaultCatalogState,
    name: 'catalog',
    reducers: {},
});

export const { reducer: catalogReducer } = catalogSlice;
